export const normaliseAnnouncementValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const normaliseAnnouncementList = (list = []) =>
  Array.isArray(list)
    ? list.map((item) => normaliseAnnouncementValue(item)).filter(Boolean)
    : [];

export const getAnnouncementDateTime = (date, time) => {
  if (!date) return null;

  const safeTime = time || "00:00";
  const dateTime = new Date(`${date}T${safeTime}`);

  if (Number.isNaN(dateTime.getTime())) return null;

  return dateTime;
};

export const getComputedAnnouncementStatus = (
  announcement = {},
  nowTimestamp = Date.now(),
) => {
  const now = new Date(nowTimestamp);

  if (announcement.status === "draft") return "draft";
  if (announcement.status === "archived") return "archived";

  if (announcement.scheduleAnnouncement) {
    const scheduledAt = getAnnouncementDateTime(
      announcement.scheduleDate,
      announcement.scheduleTime,
    );

    if (scheduledAt && scheduledAt > now) {
      return "scheduled";
    }

    return "active";
  }

  return announcement.status || "active";
};

export const isAnnouncementInDateWindow = (
  announcement = {},
  nowTimestamp = Date.now(),
) => {
  const computedStatus = getComputedAnnouncementStatus(
    announcement,
    nowTimestamp,
  );

  if (computedStatus !== "active") return false;

  const now = new Date(nowTimestamp);

  if (announcement.scheduleAnnouncement) {
    const scheduledAt = getAnnouncementDateTime(
      announcement.scheduleDate,
      announcement.scheduleTime,
    );

    if (scheduledAt && scheduledAt > now) return false;
  }

  if (announcement.keepUntilDate) {
    const keepUntil = getAnnouncementDateTime(
      announcement.keepUntilDate,
      announcement.keepUntilTime || "23:59",
    );

    if (keepUntil && keepUntil < now) return false;
  }

  return true;
};

export const announcementMatchesUser = ({
  announcement = {},
  userRole = "",
  userPermissions = [],
  adviserStatus = [],
  adminStatus = [],
}) => {
  const role = normaliseAnnouncementValue(userRole);

  if (role === "staff") return true;

  const targets = normaliseAnnouncementList(announcement.announcementFor);

  if (!targets.length) return false;

  const permissions = normaliseAnnouncementList(userPermissions);
  const adviserStatuses = normaliseAnnouncementList(adviserStatus);
  const adminStatuses = normaliseAnnouncementList(adminStatus);

  const getAdminMappedTargets = (items = []) => {
    return Array.from(
      new Set(
        items.flatMap((target) => {
          if (!target) return [];
          if (target.startsWith("ns")) return [target];

          return [target, `ns${target}`];
        }),
      ),
    );
  };

  const hasTarget = (...values) =>
    values.some((value) => targets.includes(normaliseAnnouncementValue(value)));

  const matchesAnyTarget = (values = []) =>
    values.some((value) => targets.includes(value));

  const roleAliases = {
    advisers: ["advisers", "adviser"],
    administrators: ["administrators", "administrator", "admin"],
    staff: ["staff"],
    external: ["external"],
  };

  const roleMatch = hasTarget(...(roleAliases[role] || []));

  const permissionOrStatusMatch = matchesAnyTarget([
    ...permissions,
    ...adviserStatuses,
    ...adminStatuses,
  ]);

  const adminStatusMappedMatch =
    role === "administrators" || role === "external"
      ? adminStatuses.some((status) =>
          getAdminMappedTargets(targets).includes(status),
        )
      : false;

  if (role === "advisers" && hasTarget("all advisers")) {
    return true;
  }

  return roleMatch || permissionOrStatusMatch || adminStatusMappedMatch;
};

export const getVisibleAnnouncementsForUser = ({
  announcements = [],
  userRole = "",
  userPermissions = [],
  adviserStatus = [],
  adminStatus = [],
  nowTimestamp = Date.now(),
}) => {
  return announcements.filter((announcement) => {
    return (
      isAnnouncementInDateWindow(announcement, nowTimestamp) &&
      announcementMatchesUser({
        announcement,
        userRole,
        userPermissions,
        adviserStatus,
        adminStatus,
      })
    );
  });
};

export const getAnnouncementAction = (announcement = {}) => {
  const documentFile = announcement.documentFiles?.[0];
  const url = announcement.url?.trim();

  if (documentFile?.url) {
    return {
      type: "document",
      label: documentFile.name || "Open document",
      url: documentFile.url,
    };
  }

  if (url) {
    return {
      type: "url",
      label: "Open link",
      url,
    };
  }

  return null;
};
